import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { QUIZ_AVATARS, quizSpeedRound, type QuizBattleRoomSummary, type QuizPlayerPublic } from '@shum/shared';
import { api } from '../lib/api';
import { useQuizBattleSocket } from '../lib/quizSocket';
import { QrCode } from '../components/QrCode';
import { ReconnectButton } from '../components/ReconnectButton';

export function QuizRoomLive() {
  const { code } = useParams<{ code: string }>();
  const [summary, setSummary] = useState<QuizBattleRoomSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const { state, join, start, answer, advance, superChoice, playAgain, clearError, clearReplaced, reconnect } =
    useQuizBattleSocket(code);

  useEffect(() => {
    if (!code) return;
    setSummary(null);
    setSummaryError(null);
    api
      .getQuizRoom(code.toUpperCase())
      .then((s) => setSummary(s))
      .catch((e) => setSummaryError((e as Error).message));
  }, [code]);

  if (summaryError) {
    return (
      <Shell>
        <div className="qb-center">
          <span className="qb-label">Комната не найдена</span>
          <h1 className="qb-h1">Такой комнаты нет</h1>
          <p className="qb-muted">{summaryError}</p>
          <Link to="/quiz-live" className="btn-primary lg">Создать новую →</Link>
        </div>
      </Shell>
    );
  }

  if (!summary) {
    return (
      <Shell>
        <div className="qb-center"><span className="qb-label">Подключаемся…</span><div className="qb-spinner" /></div>
      </Shell>
    );
  }

  const players: QuizPlayerPublic[] = state.lobby?.players ?? summary.players;
  const me = players.find((p) => p.playerId === state.playerId) ?? null;
  const isHost = Boolean(me?.isCreator);
  const phase = state.phase?.phase ?? summary.phase;
  const levelIndex = state.phase?.levelIndex ?? summary.levelIndex;
  const pack = state.lobby?.pack ?? summary.pack;

  if (state.replaced) {
    return (
      <Shell>
        <div className="qb-center">
          <span className="qb-label">Сессия перенесена</span>
          <h1 className="qb-h1">Этот игрок открыт на другом устройстве</h1>
          <p className="qb-muted">Войдите заново на этом устройстве, чтобы продолжить.</p>
          <button className="btn-primary lg" onClick={clearReplaced}>Понятно</button>
        </div>
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell status={state.status} onReconnect={reconnect}>
        <JoinScreen
          code={summary.code ?? code ?? ''}
          packTitle={pack?.title ?? ''}
          packEmoji={pack?.emoji ?? '🎯'}
          gameStarted={phase !== 'LOBBY'}
          busy={state.status !== 'open'}
          error={state.error?.message ?? null}
          onJoin={join}
        />
      </Shell>
    );
  }

  return (
    <Shell status={state.status} onReconnect={reconnect}>
      {state.status === 'closed' && <div className="qb-banner">Нет связи. Переподключаемся…</div>}
      {state.error && <div className="qb-toast" onClick={clearError}>{state.error.message} ✕</div>}

      {phase === 'LOBBY' && (
        <LobbyScreen
          summary={summary}
          players={players}
          meId={me.playerId}
          canStart={state.lobby?.canStart ?? summary.canStart}
          isHost={isHost}
          onStart={start}
        />
      )}

      {phase === 'LEVEL_INTRO' && (
        <LevelIntroScreen pack={pack} levelIndex={levelIndex} endsAt={state.phase?.endsAt ?? null} />
      )}

      {phase === 'QUESTION' && <QuestionScreen state={state} onAnswer={answer} />}

      {phase === 'REVEAL' && <RevealScreen state={state} />}

      {phase === 'LEVEL_STATS' && (
        <LevelStatsScreen
          stats={state.levelStats}
          meId={me.playerId}
          isHost={isHost}
          onAdvance={advance}
        />
      )}

      {phase === 'SUPER_OFFER' && (
        <SuperOfferScreen
          offer={state.superOffer}
          meId={me.playerId}
          isHost={isHost}
          onChoice={superChoice}
        />
      )}

      {phase === 'GAME_OVER' && (
        <GameOverScreen
          state={state}
          summary={summary}
          players={players}
          meId={me.playerId}
          isHost={isHost}
          onPlayAgain={playAgain}
        />
      )}
    </Shell>
  );
}

// ---------- Присоединение: имя + аватар ----------

function JoinScreen({
  code,
  packTitle,
  packEmoji,
  gameStarted,
  busy,
  error,
  onJoin,
}: {
  code: string;
  packTitle: string;
  packEmoji: string;
  gameStarted: boolean;
  busy: boolean;
  error: string | null;
  onJoin: (name: string, avatar: string) => boolean;
}) {
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<string>(() => QUIZ_AVATARS[Math.floor(Math.random() * QUIZ_AVATARS.length)]);

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    onJoin(trimmed, avatar);
  }

  return (
    <div className="qb-join">
      <span className="qb-label">Комната {code} · {packEmoji} {packTitle}</span>
      <h1 className="qb-h1">Как тебя звать?</h1>
      <p className="qb-muted">
        {gameStarted ? 'Игра уже идёт — подключишься к текущему уровню.' : 'Придумай любое имя и выбери аватар.'}
      </p>

      <div className="qb-avatars">
        {QUIZ_AVATARS.map((a) => (
          <button
            key={a}
            className={`qb-avatar${a === avatar ? ' on' : ''}`}
            onClick={() => setAvatar(a)}
            aria-pressed={a === avatar}
            type="button"
          >
            {a}
          </button>
        ))}
      </div>

      <form
        className="qb-join-form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="qb-chosen">
          <span className="qb-chosen-avatar">{avatar}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={20}
            autoFocus
            placeholder="Имя в игре"
            enterKeyHint="done"
          />
        </div>
        <button className="btn-primary lg qb-join-btn" type="submit" disabled={!name.trim() || busy}>
          В бой →
        </button>
      </form>
      {error && <div className="qb-err">{error}</div>}
    </div>
  );
}

// ---------- Лобби ----------

function LobbyScreen({
  summary,
  players,
  meId,
  canStart,
  isHost,
  onStart,
}: {
  summary: QuizBattleRoomSummary;
  players: QuizPlayerPublic[];
  meId: string;
  canStart: boolean;
  isHost: boolean;
  onStart: () => void;
}) {
  const joinUrl = `${window.location.origin}/q/${summary.code}`;
  return (
    <div className="qb-lobby">
      <div className="qb-lobby-left">
        <span className="qb-label">Лобби · ждём игроков</span>
        <h1 className="qb-h1">
          {summary.pack?.emoji} {summary.pack?.title}
        </h1>
        <p className="qb-muted">{summary.pack?.desc}</p>
        {isHost && (
          <>
            <QrCode value={joinUrl} />
            <div className="qb-join-url mono">шум.клуб → код {summary.code}</div>
          </>
        )}
        {!isHost && <p className="qb-wait">Ждём, когда ведущий начнёт игру…</p>}
      </div>

      <div className="qb-panel">
        <div className="qb-panel-head">
          <span className="qb-label">Игроки · {players.length}</span>
        </div>
        <div className="qb-player-grid">
          {players.map((p) => (
            <div key={p.playerId} className={`qb-player${p.playerId === meId ? ' me' : ''}`} data-off={p.status === 'disconnected'}>
              <span className="qb-player-avatar">{p.avatar}</span>
              <span className="qb-player-name">{p.name}</span>
              {p.isCreator && <span className="qb-star" title="Ведущий">★</span>}
            </div>
          ))}
          {players.length === 0 && <p className="qb-muted">Пока никого. Зайдите по QR или коду.</p>}
        </div>
        {isHost && (
          <button className="btn-primary block lg" disabled={!canStart} onClick={onStart} type="button">
            {canStart ? 'Начать битву →' : 'Ждём игроков…'}
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Уровень: интро ----------

function LevelIntroScreen({
  pack,
  levelIndex,
  endsAt,
}: {
  pack: QuizBattleRoomSummary['pack'];
  levelIndex: number;
  endsAt: string | null;
}) {
  const left = useCountdownSeconds(endsAt);
  const rules = quizSpeedRound(levelIndex)
    ? 'Самый быстрый верный ответ — 2 балла, остальные верные — 1 балл.'
    : 'Каждый верный ответ — 1 балл. Скорость не важна.';
  return (
    <div className="qb-center qb-level-intro">
      <span className="qb-label">Уровень {levelIndex + 1} из {pack?.levelCount ?? '?'} · начинается через {left}</span>
      <div className="qb-level-emoji">{pack?.emoji}</div>
      <h1 className="qb-h1">{pack?.title}</h1>
      <p className="qb-muted">{rules}</p>
    </div>
  );
}

// ---------- Вопрос ----------

function QuestionScreen({
  state,
  onAnswer,
}: {
  state: ReturnType<typeof useQuizBattleSocket>['state'];
  onAnswer: (optionIndex: number) => boolean;
}) {
  const q = state.question;
  const [picked, setPicked] = useState<number | null>(null);
  const identity = `${q?.levelIndex}:${q?.questionIndex}`;
  const lastIdentity = useRef(identity);
  useEffect(() => {
    if (lastIdentity.current !== identity) {
      lastIdentity.current = identity;
      setPicked(null);
    }
  }, [identity]);

  const msLeft = useCountdownMs(state.phase?.endsAt ?? q?.endsAt ?? null);
  const totalMs = q ? Math.max(1, new Date(q.endsAt).getTime() - new Date(q.startedAt).getTime()) : 1;
  const secondsLeft = Math.ceil(msLeft / 1000);
  const isSuper = Boolean(q?.superGame);
  const iRisked = Boolean(
    isSuper && state.superOffer?.players.find((p) => p.playerId === state.playerId)?.accepted,
  );
  const canAnswer = !isSuper || iRisked;

  // После реконнекта сервер помнит ответ, а клиент — нет: отмечаем «принято».
  useEffect(() => {
    if (state.error?.code === 'ALREADY_ANSWERED') setPicked((p) => (p === null ? -2 : p));
  }, [state.error]);

  if (!q) {
    return (
      <div className="qb-center">
        <span className="qb-label">Готовим вопрос…</span>
        <div className="qb-spinner" />
      </div>
    );
  }

  function pick(i: number) {
    if (picked !== null || !canAnswer) return;
    const ok = onAnswer(i);
    if (ok) setPicked(i);
  }

  return (
    <div className="qb-question">
      <div className="qb-q-top">
        <span className="qb-label">
          {isSuper
            ? '⚡️ Супер-игра · финальный вопрос'
            : `Уровень ${q.levelIndex + 1} · вопрос ${q.questionIndex + 1} из ${q.totalInLevel}`}
        </span>
        <span className={`qb-timer${secondsLeft <= 5 ? ' hot' : ''}`}>{secondsLeft}</span>
      </div>
      <div className="qb-timer-track">
        <div className="qb-timer-fill" style={{ width: `${Math.max(0, Math.min(100, (msLeft / totalMs) * 100))}%` }} />
      </div>

      {isSuper && (
        <div className="qb-super-stake">
          {iRisked
            ? 'На кону все твои баллы: верный ответ удваивает их, неверный сжигает дотла.'
            : 'Ты не рискнул — просто наблюдай, твои баллы в безопасности.'}
        </div>
      )}

      <h2 className="qb-q-text">{q.question.q}</h2>

      <div className="qb-options">
        {q.question.options.map((opt, i) => (
          <button
            key={i}
            type="button"
            className={`qb-opt o${i % 4}${picked === i ? ' picked' : ''}`}
            onClick={() => pick(i)}
            disabled={picked !== null || !canAnswer}
          >
            <span className="qb-opt-key">{String.fromCharCode(65 + i)}</span>
            <span className="qb-opt-text">{opt}</span>
          </button>
        ))}
      </div>

      {picked !== null ? (
        <div className="qb-picked">Ответ принят — жди остальных</div>
      ) : canAnswer ? (
        state.answered && <div className="qb-answered">Ответили: {state.answered.answeredCount} из {state.answered.playerCount}</div>
      ) : (
        isSuper && <div className="qb-picked">Супер-игру играют рискнувшие</div>
      )}
    </div>
  );
}

// ---------- Раскрытие ответа ----------

function RevealScreen({ state }: { state: ReturnType<typeof useQuizBattleSocket>['state'] }) {
  const reveal = state.reveal;
  const q = state.question;
  if (!reveal) {
    return (
      <div className="qb-center"><span className="qb-label">Секунду…</span><div className="qb-spinner" /></div>
    );
  }
  const mine = reveal.your;
  const isSuper = Boolean(reveal.superGame);

  let resultText: string | null = null;
  let resultKind: 'ok' | 'bad' | 'miss' = 'miss';
  if (isSuper && mine) {
    if (mine.super === 'risked') {
      if (mine.correct) {
        resultText = `Верно! Баллы удвоены — теперь у тебя ${mine.score}`;
        resultKind = 'ok';
      } else if (mine.answered) {
        resultText = 'Мимо! Все баллы сгорели в ноль';
        resultKind = 'bad';
      } else {
        resultText = 'Не ответил — все баллы сгорели в ноль';
        resultKind = 'bad';
      }
    } else {
      resultText = `Ты не рискнул — счёт не изменился (${mine.score})`;
      resultKind = 'miss';
    }
  } else if (mine) {
    if (!mine.answered) {
      resultText = 'Не успел ответить';
      resultKind = 'miss';
    } else if (mine.correct) {
      resultText = `Верно! +${mine.points} ${pluralPoints(mine.points)} · всего ${mine.score}`;
      resultKind = 'ok';
    } else {
      resultText = 'Мимо — 0 баллов';
      resultKind = 'bad';
    }
  }

  return (
    <div className="qb-question">
      <div className="qb-q-top">
        <span className="qb-label">
          {isSuper ? '⚡️ Супер-игра · финальный вопрос' : `Уровень ${reveal.levelIndex + 1} · вопрос ${reveal.questionIndex + 1}`}
        </span>
      </div>
      <h2 className="qb-q-text">{q?.question.q ?? ''}</h2>

      <div className="qb-options reveal">
        {q?.question.options.map((opt, i) => {
          const isCorrect = i === reveal.correctIndex;
          const isMine = mine?.answered && mine.optionIndex === i;
          return (
            <div key={i} className={`qb-opt o${i % 4}${isCorrect ? ' correct' : ''}${isMine && !isCorrect ? ' wrong' : ''}${!isCorrect && !isMine ? ' dim' : ''}`}>
              <span className="qb-opt-key">{String.fromCharCode(65 + i)}</span>
              <span className="qb-opt-text">{opt}</span>
              {isCorrect && <span className="qb-opt-mark">✓</span>}
              {isMine && !isCorrect && <span className="qb-opt-mark">✕</span>}
            </div>
          );
        })}
      </div>

      {resultText && <div className={`qb-result ${resultKind}`}>{resultText}</div>}

      {reveal.fact && <div className="qb-fact">{reveal.fact}</div>}
    </div>
  );
}

/** 1 балл, 2 балла, 5 баллов. */
function pluralPoints(n: number): string {
  const abs = Math.abs(n) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return 'баллов';
  if (d > 1 && d < 5) return 'балла';
  if (d === 1) return 'балл';
  return 'баллов';
}

// ---------- Статистика между уровнями ----------

function LevelStatsScreen({
  stats,
  meId,
  isHost,
  onAdvance,
}: {
  stats: ReturnType<typeof useQuizBattleSocket>['state']['levelStats'];
  meId: string;
  isHost: boolean;
  onAdvance: () => void;
}) {
  if (!stats) {
    return (
      <div className="qb-center"><span className="qb-label">Считаем очки…</span><div className="qb-spinner" /></div>
    );
  }
  return (
    <div className="qb-stats">
      <div className="qb-stats-head">
        <span className="qb-label">
          Уровень {stats.levelIndex + 1} из {stats.totalLevels} пройден
        </span>
        <h1 className="qb-h1">
          {stats.levelEmoji} {stats.levelTitle}
        </h1>
        <p className="qb-muted">Баллы за уровень · точность · общий счёт</p>
      </div>

      <div className="qb-stats-rows">
        {stats.rows.map((row, i) => (
          <div key={row.playerId} className={`qb-stat-row${row.playerId === meId ? ' me' : ''}`}>
            <span className="qb-place">{i + 1}</span>
            <span className="qb-stat-avatar">{row.avatar}</span>
            <span className="qb-stat-name">{row.name}</span>
            <span className="qb-stat-acc" title="Верных ответов на уровне">
              {row.levelTotal > 0 ? `${row.levelCorrect}/${row.levelTotal}` : '—'}
            </span>
            <span className="qb-stat-level">+{row.levelPoints}</span>
            <span className="qb-stat-total">{row.totalScore}</span>
          </div>
        ))}
      </div>

      {isHost ? (
        <button className="btn-primary lg" onClick={onAdvance} type="button">
          {stats.isFinal ? 'К супер-игре →' : 'Следующий уровень →'}
        </button>
      ) : (
        <p className="qb-wait">
          {stats.isFinal
            ? 'Впереди супер-игра — начнёт ведущий'
            : 'Следующий уровень начнёт ведущий'}
        </p>
      )}
    </div>
  );
}

// ---------- Супер-игра: согласие ----------

function SuperOfferScreen({
  offer,
  meId,
  isHost,
  onChoice,
}: {
  offer: ReturnType<typeof useQuizBattleSocket>['state']['superOffer'];
  meId: string;
  isHost: boolean;
  onChoice: (accept: boolean) => boolean;
}) {
  const left = useCountdownSeconds(offer?.endsAt ?? null);
  if (!offer) {
    return (
      <div className="qb-center"><span className="qb-label">Готовим супер-игру…</span><div className="qb-spinner" /></div>
    );
  }
  const me = offer.players.find((p) => p.playerId === meId);
  const decided = offer.players.filter((p) => p.decided).length;
  return (
    <div className="qb-stats">
      <div className="qb-stats-head">
        <span className="qb-label">Финал · решили {decided} из {offer.players.length}</span>
        <h1 className="qb-h1">⚡️ Супер-игра</h1>
        <p className="qb-muted">
          Один последний вопрос. Согласишься — верный ответ удвоит твои баллы,
          а неверный сожжёт их в ноль. Откажешься — счёт останется как есть.
        </p>
        {me && (
          <p className="qb-super-mine">
            Твой счёт: <strong>{me.score}</strong> {pluralPoints(me.score)}
          </p>
        )}
      </div>

      {me && !me.decided && (
        <div className="qb-super-actions">
          <button className="btn-primary lg" onClick={() => onChoice(true)} type="button">
            Рискую 🔥
          </button>
          <button className="btn-ghost lg" onClick={() => onChoice(false)} type="button">
            Сохранить счёт
          </button>
        </div>
      )}
      {me?.decided && (
        <p className="qb-picked">
          {me.accepted ? 'Ты в игре — жди вопрос' : 'Ты пас — жди рискнувших'} · молчуны пасуют через {left} с
        </p>
      )}

      <div className="qb-stats-rows">
        {offer.players.map((p, i) => (
          <div key={p.playerId} className={`qb-stat-row super${p.playerId === meId ? ' me' : ''}`}>
            <span className="qb-place">{i + 1}</span>
            <span className="qb-stat-avatar">{p.avatar}</span>
            <span className="qb-stat-name">{p.name}</span>
            <span className="qb-stat-total">{p.score}</span>
            <span className={`qb-super-chip${p.accepted ? ' yes' : p.decided ? ' no' : ''}`}>
              {p.accepted ? 'рискует' : p.decided ? 'пас' : 'думает…'}
            </span>
          </div>
        ))}
      </div>

      {isHost && <p className="qb-wait">Как только все решат (или кончится время) — супер-вопрос появится у всех.</p>}
    </div>
  );
}

// ---------- Финал ----------

function GameOverScreen({
  state,
  summary,
  players,
  meId,
  isHost,
  onPlayAgain,
}: {
  state: ReturnType<typeof useQuizBattleSocket>['state'];
  summary: QuizBattleRoomSummary;
  players: QuizPlayerPublic[];
  meId: string;
  isHost: boolean;
  onPlayAgain: () => void;
}) {
  const over = state.gameOver;
  const rows = over?.rows ?? [];
  const iWon = over?.winnerPlayerId === meId;
  const podium = rows.slice(0, 3);
  const podiumOrder = podium.length === 3 ? [podium[1], podium[0], podium[2]] : podium;
  const podiumHeights = podium.length === 3 ? [96, 140, 72] : podium.length === 2 ? [104, 140] : [140];

  if (!over && rows.length === 0) {
    return (
      <div className="qb-center"><span className="qb-label">Подводим итоги…</span><div className="qb-spinner" /></div>
    );
  }

  return (
    <div className="qb-final">
      <div className="qb-center">
        <span className="qb-label">Финиш · {summary.pack?.title}</span>
        <h1 className="qb-h1">
          {over?.winnerName ? `🏆 ${over.winnerName} победил!` : '🏆 Ничья на вершине'}
        </h1>
        {iWon && <p className="qb-iwon">Это ты. Красиво.</p>}
      </div>

      {podium.length > 0 && (
        <div className="qb-podium">
          {podiumOrder.map((row, i) => (
            <div key={row.playerId} className={`qb-pod${podiumHeights[i] === 140 ? ' first' : ''}${row.playerId === meId ? ' me' : ''}`}>
              <span className="qb-pod-avatar">{row.avatar}</span>
              <span className="qb-pod-name">{row.name}</span>
              <span className="qb-pod-score">{row.score}</span>
              <div className="qb-pod-col" style={{ height: podiumHeights[i] }} />
            </div>
          ))}
        </div>
      )}

      {rows.length > 3 && (
        <div className="qb-stats-rows">
          {rows.slice(3).map((row, i) => (
            <div key={row.playerId} className={`qb-stat-row${row.playerId === meId ? ' me' : ''}`}>
              <span className="qb-place">{i + 4}</span>
              <span className="qb-stat-avatar">{row.avatar}</span>
              <span className="qb-stat-name">{row.name}</span>
              <span className="qb-stat-acc" title="Верных ответов">
                {row.answeredTotal > 0 ? `${row.correctTotal}/${row.answeredTotal}` : '—'}
              </span>
              <span className="qb-stat-total">{row.score}</span>
            </div>
          ))}
        </div>
      )}

      {isHost ? (
        <div className="qb-actions">
          <button className="btn-primary lg" onClick={onPlayAgain} type="button">Играть снова</button>
          <Link className="btn-ghost lg" to="/quiz-live">Другая сборка →</Link>
        </div>
      ) : (
        <p className="qb-wait">Ведущий может запустить игру заново или взять другую сборку.</p>
      )}
      <p className="qb-muted">{players.length} игроков · до встречи в следующей битве</p>
    </div>
  );
}

// ---------- Общая обёртка и утилиты ----------

function Shell({
  children,
  status,
  onReconnect,
}: {
  children: React.ReactNode;
  status?: 'connecting' | 'open' | 'closed';
  onReconnect?: () => void;
}) {
  return (
    <div className="qb-root">
      <QuizRoomStyles />
      {status && onReconnect && <ReconnectButton status={status} onReconnect={onReconnect} />}
      <header className="bar">
        <div className="bar-row" style={{ padding: '14px 24px' }}>
          <Link to="/" className="logo">
            <span className="logo-mark">Ш</span>
            <span className="logo-name">Шум.клуб</span>
          </Link>
          <Link to="/quiz-live" className="qb-label" style={{ textDecoration: 'none' }}>
            Квиз-баттл
          </Link>
        </div>
      </header>
      <main className="qb-main">{children}</main>
    </div>
  );
}

function useCountdownMs(endsAt: string | null): number {
  const target = useMemo(() => (endsAt ? new Date(endsAt).getTime() : null), [endsAt]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (target === null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 150);
    return () => window.clearInterval(id);
  }, [target]);
  if (target === null) return 0;
  return Math.max(0, target - now);
}

function useCountdownSeconds(endsAt: string | null): number {
  return Math.ceil(useCountdownMs(endsAt) / 1000);
}

// ---------- Стили ----------

function QuizRoomStyles() {
  return (
    <style>{`
      .qb-root { min-height: 100vh; background: var(--bg); color: var(--ink); }
      .qb-main { max-width: 960px; margin: 0 auto; padding: 32px 20px 64px; }
      .qb-label { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-dim); display: inline-block; }
      .qb-h1 { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 48px; line-height: 1.05; letter-spacing: -.01em; margin: 12px 0 12px; }
      .qb-muted { color: var(--ink-dim); font-size: 15px; line-height: 1.55; margin: 0 0 20px; }
      .qb-center { display: flex; flex-direction: column; align-items: flex-start; padding: 48px 0; }
      .qb-spinner { width: 34px; height: 34px; border: 3px solid var(--line); border-top-color: var(--accent); border-radius: 50%; animation: qb-spin 1s linear infinite; margin-top: 20px; }
      @keyframes qb-spin { to { transform: rotate(360deg); } }
      .qb-banner { position: sticky; top: 0; z-index: 20; background: rgba(224, 106, 106, .92); color: #fff; text-align: center; padding: 8px 16px; font-size: 13px; }
      .qb-toast { position: fixed; bottom: 18px; left: 50%; transform: translateX(-50%); z-index: 30; background: var(--bg2); border: 1px solid var(--line-strong); color: var(--ink); border-radius: 8px; padding: 10px 18px; font-size: 13px; cursor: pointer; box-shadow: 0 8px 30px rgba(0,0,0,.35); }
      .qb-err { color: #e06a6a; font-size: 14px; margin-top: 14px; }

      /* Вход */
      .qb-join { padding-top: 24px; }
      .qb-avatars { display: grid; grid-template-columns: repeat(8, 1fr); gap: 8px; margin: 24px 0; }
      .qb-avatar { font-size: 26px; padding: 10px 0; background: var(--bg2); border: 1px solid var(--line); border-radius: 10px; cursor: pointer; transition: border-color .15s, transform .15s; }
      .qb-avatar:hover { border-color: var(--line-strong); transform: scale(1.06); }
      .qb-avatar.on { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 14%, var(--bg2)); transform: scale(1.1); }
      .qb-join-form { display: flex; flex-direction: column; gap: 14px; max-width: 420px; }
      .qb-chosen { display: flex; align-items: center; gap: 12px; background: var(--bg2); border: 1px solid var(--line); border-radius: var(--radius-card); padding: 12px 16px; }
      .qb-chosen-avatar { font-size: 30px; line-height: 1; }
      .qb-chosen input { flex: 1; background: transparent; border: 0; outline: none; color: var(--ink); font-size: 18px; font-family: inherit; }
      .qb-join-btn { width: 100%; }

      /* Лобби */
      .qb-lobby { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: start; }
      .qb-join-url { margin-top: 14px; font-size: 12px; color: var(--ink-dim); letter-spacing: .08em; }
      .qb-wait { color: var(--ink-dim); font-size: 14px; }
      .qb-panel { background: var(--bg2); border: 1px solid var(--line); border-radius: var(--radius-card); padding: 24px; }
      .qb-panel-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 16px; }
      .qb-player-grid { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
      .qb-player { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid var(--line); border-radius: 10px; }
      .qb-player.me { border-color: var(--accent); }
      .qb-player[data-off='true'] { opacity: .45; }
      .qb-player-avatar { font-size: 24px; line-height: 1; }
      .qb-player-name { font-size: 16px; font-weight: 500; }
      .qb-star { margin-left: auto; color: var(--accent); }

      /* Интро уровня */
      .qb-level-intro { align-items: center; text-align: center; padding-top: 96px; }
      .qb-level-emoji { font-size: 96px; line-height: 1; margin: 24px 0; }

      /* Вопрос */
      .qb-question { padding-top: 8px; }
      .qb-q-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
      .qb-timer { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 34px; color: var(--accent); min-width: 52px; text-align: right; }
      .qb-timer.hot { color: #e06a6a; }
      .qb-timer-track { height: 5px; background: var(--line); border-radius: 3px; overflow: hidden; margin-bottom: 28px; }
      .qb-timer-fill { height: 100%; background: var(--accent); transition: width .15s linear; }
      .qb-q-text { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 34px; line-height: 1.15; letter-spacing: -.005em; margin: 0 0 24px; }
      .qb-options { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .qb-opt { position: relative; display: flex; align-items: center; gap: 14px; text-align: left; padding: 20px 18px; min-height: 84px;
        background: var(--bg2); border: 1px solid var(--line); border-left-width: 4px; border-radius: var(--radius-card); color: var(--ink);
        cursor: pointer; font: inherit; font-size: 16px; line-height: 1.35; transition: transform .12s, border-color .15s, opacity .15s, background .15s; }
      .qb-opt:hover:not(:disabled) { transform: translateY(-2px); }
      .qb-opt.o0 { border-left-color: #e06a6a; }
      .qb-opt.o1 { border-left-color: #6a9fe0; }
      .qb-opt.o2 { border-left-color: #e0c36a; }
      .qb-opt.o3 { border-left-color: #7ec96b; }
      .qb-opt.picked { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 12%, var(--bg2)); }
      .qb-opt:disabled { cursor: default; }
      .qb-options.reveal .qb-opt { cursor: default; }
      .qb-opt.correct { border-color: #7ec96b; background: color-mix(in srgb, #7ec96b 16%, var(--bg2)); }
      .qb-opt.wrong { border-color: #e06a6a; }
      .qb-opt.dim { opacity: .4; }
      .qb-opt-key { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .1em; color: var(--ink-faint);
        border: 1px solid var(--line-strong); border-radius: 3px; padding: 4px 7px; flex-shrink: 0; }
      .qb-opt-mark { margin-left: auto; font-size: 18px; font-weight: 700; }
      .qb-opt.correct .qb-opt-mark { color: #7ec96b; }
      .qb-opt.wrong .qb-opt-mark { color: #e06a6a; }
      .qb-picked { margin-top: 20px; font-size: 15px; color: var(--accent); }
      .qb-answered { margin-top: 20px; font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .12em; text-transform: uppercase; color: var(--ink-faint); }
      .qb-result { margin-top: 22px; font-size: 17px; font-weight: 500; padding: 14px 18px; border-radius: 10px; }
      .qb-result.ok { color: #7ec96b; background: color-mix(in srgb, #7ec96b 12%, transparent); }
      .qb-result.bad { color: #e06a6a; background: color-mix(in srgb, #e06a6a 12%, transparent); }
      .qb-result.miss { color: var(--ink-dim); background: var(--bg2); }
      .qb-fact { margin-top: 16px; font-size: 14px; line-height: 1.55; color: var(--ink-dim); border-left: 2px solid var(--accent); padding: 4px 0 4px 16px; }

      /* Статистика уровня */
      .qb-stats { padding-top: 16px; }
      .qb-stats-head { margin-bottom: 24px; }
      .qb-stats-rows { display: flex; flex-direction: column; gap: 8px; margin-bottom: 28px; }
      .qb-stat-row { display: grid; grid-template-columns: 30px 40px 1fr 70px 90px 90px; align-items: center; gap: 12px;
        padding: 12px 16px; border: 1px solid var(--line); border-radius: 10px; background: var(--bg2); }
      .qb-stat-row.super { grid-template-columns: 30px 40px 1fr 90px 110px; }
      .qb-stat-row.me { border-color: var(--accent); }
      .qb-place { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--ink-faint); }
      .qb-stat-avatar { font-size: 24px; line-height: 1; }
      .qb-stat-name { font-size: 16px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .qb-stat-acc { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--ink-dim); text-align: center; }
      .qb-stat-level { font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #7ec96b; text-align: right; }
      .qb-stat-total { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 20px; text-align: right; }
      .qb-auto { margin-top: 10px; }

      /* Супер-игра */
      .qb-super-stake { margin: -6px 0 18px; padding: 12px 16px; border: 1px solid var(--accent); border-radius: 10px;
        background: color-mix(in srgb, var(--accent) 10%, var(--bg2)); font-size: 15px; line-height: 1.45; }
      .qb-super-mine { font-size: 16px; margin: -6px 0 8px; }
      .qb-super-actions { display: flex; gap: 12px; flex-wrap: wrap; margin: 4px 0 24px; }
      .qb-super-chip { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .08em; text-transform: uppercase;
        color: var(--ink-dim); border: 1px solid var(--line-strong); border-radius: 999px; padding: 4px 12px; text-align: center; }
      .qb-super-chip.yes { color: #7ec96b; border-color: #7ec96b; background: color-mix(in srgb, #7ec96b 12%, transparent); }
      .qb-super-chip.no { color: var(--ink-dim); background: var(--bg2); }

      /* Финал */
      .qb-final .qb-center { align-items: center; text-align: center; padding-bottom: 8px; }
      .qb-iwon { color: var(--accent); font-size: 16px; margin: -8px 0 16px; }
      .qb-podium { display: flex; align-items: flex-end; justify-content: center; gap: 14px; margin: 24px 0 32px; }
      .qb-pod { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 110px; }
      .qb-pod-avatar { font-size: 40px; line-height: 1; }
      .qb-pod.first .qb-pod-avatar { font-size: 52px; }
      .qb-pod-name { font-size: 14px; font-weight: 500; max-width: 110px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .qb-pod-score { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--ink-dim); }
      .qb-pod-col { width: 100%; border-radius: 10px 10px 0 0; background: var(--bg2); border: 1px solid var(--line); border-bottom: 0; }
      .qb-pod.first .qb-pod-col { background: color-mix(in srgb, var(--accent) 20%, var(--bg2)); border-color: var(--accent); }
      .qb-pod.me .qb-pod-col { border-color: var(--accent); }
      .qb-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin: 8px 0 16px; }
      .qb-final .qb-stats-rows { max-width: 640px; margin-left: auto; margin-right: auto; }
      .qb-final .qb-muted { text-align: center; }

      @media (max-width: 720px) {
        .qb-main { padding: 20px 14px 48px; }
        .qb-h1 { font-size: 36px; }
        .qb-lobby { grid-template-columns: 1fr; }
        .qb-avatars { grid-template-columns: repeat(6, 1fr); }
        .qb-q-text { font-size: 25px; }
        .qb-options { grid-template-columns: 1fr; }
        .qb-opt { min-height: 64px; padding: 16px 14px; }
        .qb-stat-row { grid-template-columns: 24px 32px 1fr 60px 74px; }
        .qb-stat-total { grid-column: 5; }
        .qb-stat-level { display: none; }
        .qb-stat-row.super { grid-template-columns: 24px 32px 1fr 74px 96px; }
        .qb-pod { width: 92px; }
      }
    `}</style>
  );
}
